// PATH: src/hooks/useUnsavedGuard.ts — protection des saisies non enregistrées.
//
// Portée : fermeture d'onglet et rechargement (beforeunload). La navigation
// INTERNE n'est pas interceptée — `useBlocker` de react-router exige un routeur
// « data » (createBrowserRouter), alors que l'application monte un
// <BrowserRouter>. Les écrans concernés gardent donc leur propre garde sur les
// gestes qui font perdre la saisie (bouton Annuler, fermeture d'une modale),
// via `confirmDiscard` ci-dessous.
import { useEffect } from "react";
import { Modal } from "antd";

/**
 * useUnsavedGuard — avertit avant de quitter la page quand `dirty` est vrai.
 * Le navigateur impose son propre libellé : le texte n'est pas paramétrable.
 */
export const useUnsavedGuard = (dirty: boolean): void => {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Requis par les navigateurs anciens pour déclencher la boîte native.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
};

/**
 * confirmDiscard — demande confirmation avant d'abandonner une saisie en cours.
 * Résout `true` s'il n'y a rien à perdre, pour que l'appelant écrive un seul
 * chemin : `confirmDiscard(dirty).then((ok) => ok && close())`.
 */
export const confirmDiscard = (dirty: boolean): Promise<boolean> => {
  if (!dirty) return Promise.resolve(true);
  return new Promise((resolve) => {
    Modal.confirm({
      title: "Abandonner la saisie ?",
      content: "Les modifications non enregistrées seront perdues.",
      okText: "Abandonner",
      okButtonProps: { danger: true },
      cancelText: "Continuer la saisie",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};
